import { useState, useEffect, useRef } from "react";
import { useLocation, Link } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { DashboardHeader } from "@/components/dashboard-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Skeleton } from "@/components/ui/skeleton";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient, useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { Switch } from "@/components/ui/switch";
import { AlertCircle, CheckCircle2, Building2, Trash2, Plus, User as UserIcon, Shield, Users, Send, Check, X, Loader2, Mail, ArrowLeft, MessageSquare, UserPlus, GitCompare, SlidersHorizontal, Clock, AtSign, Copy, Link2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";
import type { Organization, User, OrgEmailNotificationSettings, OrgNotificationLogItem } from "@shared/schema";

const updateOrgSchema = z.object({
  name: z.string().min(1, "Organization name is required").max(100, "Organization name must be 100 characters or less"),
  slug: z.string().min(1, "Slug is required").max(100, "Slug must be 100 characters or less").regex(/^[a-z0-9-]+$/, "Slug may only contain lowercase letters, numbers, and hyphens"),
});

type UpdateOrgForm = z.infer<typeof updateOrgSchema>;


const LOG_PAGE_SIZE = 20;

const ALL_NOTIF_TYPES = ['comment_added', 'user_mentioned', 'user_joined_document', 'user_invitation', 'version_comparison_ready'] as const;
type LogNotifType = typeof ALL_NOTIF_TYPES[number];

const NOTIF_FILTER_OPTIONS: { type: LogNotifType; label: string; Icon: React.ElementType }[] = [
  { type: 'comment_added',           label: 'Comments',          Icon: MessageSquare },
  { type: 'user_mentioned',          label: 'Mentions',          Icon: AtSign },
  { type: 'user_joined_document',    label: 'User Joined',       Icon: UserPlus },
  { type: 'user_invitation',         label: 'Invitations',       Icon: Mail },
  { type: 'version_comparison_ready',label: 'Comparison Ready',  Icon: GitCompare },
];

function getNotifIcon(type: string) {
  switch (type) {
    case 'comment_added':            return MessageSquare;
    case 'user_mentioned':           return AtSign;
    case 'user_joined_document':     return UserPlus;
    case 'user_invitation':          return Mail;
    case 'version_comparison_ready': return GitCompare;
    default:                         return Clock;
  }
}

function getNotifLabel(type: string) {
  switch (type) {
    case 'comment_added':            return 'Comment added';
    case 'user_mentioned':           return 'Mention';
    case 'user_joined_document':     return 'User joined';
    case 'user_invitation':          return 'Invitation';
    case 'version_comparison_ready': return 'Comparison ready';
    default:                         return type;
  }
}

function getStatusBadgeClass(status: string) {
  switch (status) {
    case 'sent':    return 'bg-green-500/10 text-green-600 dark:text-green-400';
    case 'failed':  return 'bg-red-500/10 text-red-600 dark:text-red-400';
    case 'skipped': return 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400';
    default:        return 'bg-muted text-muted-foreground';
  }
}

function EmailActivityLog({ organizationId }: { organizationId: string }) {
  const [activeTypes, setActiveTypes] = useState<Set<LogNotifType>>(new Set(NOTIF_FILTER_OPTIONS.map(o => o.type)));
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setFilterOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const typesKey = Array.from(activeTypes).sort().join(',');

  const {
    data,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<OrgNotificationLogItem[]>({
    queryKey: ['/api/organizations', organizationId, 'notification-logs', 'infinite', typesKey],
    queryFn: async ({ pageParam }) => {
      const offset = (pageParam as number) ?? 0;
      const params = new URLSearchParams({
        limit: String(LOG_PAGE_SIZE),
        offset: String(offset),
      });
      if (activeTypes.size < NOTIF_FILTER_OPTIONS.length) params.set('types', typesKey);
      const res = await fetch(`/api/organizations/${organizationId}/notification-logs?${params}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch notification logs');
      return res.json();
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < LOG_PAGE_SIZE) return undefined;
      return allPages.length * LOG_PAGE_SIZE;
    },
    initialPageParam: 0,
  });

  const toggleType = (type: LogNotifType) => {
    setActiveTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) {
        if (next.size === 1) return prev;
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const allLogs = data?.pages.flat() ?? [];
  const hiddenCount = NOTIF_FILTER_OPTIONS.length - activeTypes.size;
  const hasActiveFilter = hiddenCount > 0;

  if (isLoading) {
    return (
      <div className="space-y-1 py-1">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex items-start gap-3 py-2">
            <Skeleton className="h-3.5 w-3.5 rounded mt-0.5 shrink-0" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-3 w-3/4" />
              <Skeleton className="h-2.5 w-1/4" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-0">
      <div className="flex items-center justify-between mb-2 gap-2">
        <span className="text-xs text-muted-foreground shrink-0">
          {allLogs.length} {allLogs.length === 1 ? 'email' : 'emails'}
          {hasNextPage && '+'}
        </span>

        <div className="relative" ref={filterRef}>
          <button
            onClick={() => setFilterOpen(v => !v)}
            className={cn(
              'flex items-center gap-1.5 text-xs px-2 py-1 rounded border transition-colors',
              filterOpen || hasActiveFilter
                ? 'border-border bg-accent text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-accent/60'
            )}
          >
            <SlidersHorizontal className="h-3 w-3" />
            <span>Filter</span>
            {hasActiveFilter && (
              <span className="ml-0.5 bg-primary text-primary-foreground rounded-full text-[10px] w-4 h-4 flex items-center justify-center font-medium">
                {hiddenCount}
              </span>
            )}
          </button>

          {filterOpen && (
            <div className="absolute right-0 top-full mt-1 z-20 w-52 bg-popover border border-border rounded-md shadow-md py-1">
              {NOTIF_FILTER_OPTIONS.map(({ type, label, Icon }) => {
                const active = activeTypes.has(type);
                return (
                  <button
                    key={type}
                    onClick={() => toggleType(type)}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-accent transition-colors text-left"
                  >
                    <span className={cn(
                      'flex items-center justify-center w-4 h-4 rounded border shrink-0 transition-colors',
                      active ? 'bg-primary border-primary' : 'border-border bg-transparent'
                    )}>
                      {active && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                    </span>
                    <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="text-foreground">{label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {allLogs.length === 0 ? (
        <div className="flex flex-col items-center py-6 text-center">
          <Clock className="h-5 w-5 text-muted-foreground/40 mb-2" />
          <p className="text-xs text-muted-foreground">No email activity to show</p>
        </div>
      ) : (
        <div className="space-y-0">
          {allLogs.map((log) => {
            const Icon = getNotifIcon(log.notificationType);
            return (
              <div key={log.id} className="flex items-start gap-3 py-2 px-1 hover:bg-accent/50 rounded transition-colors">
                <Icon className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-1.5 flex-wrap">
                    <span className="text-sm font-medium text-foreground leading-snug">{log.recipientName ?? log.recipientEmail ?? 'Unknown recipient'}</span>
                    {log.recipientName && log.recipientEmail && (
                      <span className="text-[11px] text-muted-foreground/70 truncate">{log.recipientEmail}</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground/60 truncate mt-0.5">{getNotifLabel(log.notificationType)}{log.documentName ? ` · ${log.documentName}` : ''}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={cn('text-[10px] font-medium px-1.5 py-0.5 rounded-full', getStatusBadgeClass(log.status))}>
                      {log.status.charAt(0).toUpperCase() + log.status.slice(1)}
                    </span>
                    <span className="font-mono text-[11px] text-muted-foreground/50">
                      {formatDistanceToNow(new Date(log.sentAt), { addSuffix: true })}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {hasNextPage && (
        <div className="flex justify-center pt-2 pb-1">
          <button
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground border border-border rounded px-3 py-1.5 hover:bg-accent transition-colors disabled:opacity-50"
          >
            {isFetchingNextPage ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                <span>Loading…</span>
              </>
            ) : (
              <span>Load more</span>
            )}
          </button>
        </div>
      )}

      {!hasNextPage && allLogs.length > 0 && (
        <p className="text-center text-[11px] text-muted-foreground/40 py-3 font-mono">all caught up</p>
      )}
    </div>
  );
}

export default function OrgSettings() {
  const [, setLocation] = useLocation();
  const [isSuccess, setIsSuccess] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteMode, setInviteMode] = useState<"email" | "link">("email");
  const [emailInputValue, setEmailInputValue] = useState("");
  const [emailChips, setEmailChips] = useState<string[]>([]);
  const [inviteResults, setInviteResults] = useState<{ email: string; success: boolean; error?: string }[] | null>(null);
  const [justResentInvite, setJustResentInvite] = useState<string | null>(null);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [inviteLinkCopied, setInviteLinkCopied] = useState(false);
  const { toast } = useToast();
  const { user: currentUser, isAuthenticated, isLoading } = useAuth();
  const queryClient = useQueryClient();

  const { data: organization, isLoading: isOrgLoading } = useQuery<Organization>({
    queryKey: ["/api/organizations", currentUser?.organizationId],
    enabled: !!currentUser?.organizationId,
  });

  const { data: users = [], isLoading: isUsersLoading } = useQuery<User[]>({
    queryKey: ["/api/organizations", currentUser?.organizationId, "users"],
    enabled: !!currentUser?.organizationId,
  });

  const { data: pendingInvites = [] } = useQuery<{ token: string; targetEmail: string | null; createdAt: string | null }[]>({
    queryKey: ["/api/organizations", currentUser?.organizationId, "pending-invites"],
    enabled: !!currentUser?.organizationId && currentUser?.role === "admin",
  });

  const { data: notificationSettings, isLoading: isNotifLoading } = useQuery<OrgEmailNotificationSettings>({
    queryKey: ["/api/organizations", currentUser?.organizationId, "notification-settings"],
    enabled: !!currentUser?.organizationId,
  });

  const updateNotifMutation = useMutation({
    mutationFn: async (updates: Partial<OrgEmailNotificationSettings>) => {
      const response = await fetch(`/api/organizations/${currentUser?.organizationId}/notification-settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to update notification settings");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/organizations", currentUser?.organizationId, "notification-settings"],
      });
      toast({
        title: "Notification settings updated",
        description: "Email notification preferences have been saved.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Update failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const form = useForm<UpdateOrgForm>({
    resolver: zodResolver(updateOrgSchema),
    defaultValues: {
      name: "",
      slug: "",
    },
  });

  useEffect(() => {
    if (organization) {
      form.reset({ name: organization.name, slug: organization.slug ?? "" });
    }
  }, [organization, form]);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      toast({
        title: "Unauthorized",
        description: "You are logged out. Logging in again...",
        variant: "destructive",
      });
      setTimeout(() => {
        window.location.href = "/login";
      }, 500);
      return;
    }
  }, [isAuthenticated, isLoading, toast]);


  const updateOrgMutation = useMutation({
    mutationFn: async (data: UpdateOrgForm) => {
      const response = await fetch(`/api/organizations/${currentUser?.organizationId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to update organization");
      }
      return response.json();
    },
    onSuccess: () => {
      setIsSuccess(true);
      setTimeout(() => setIsSuccess(false), 3000);
      queryClient.invalidateQueries({ queryKey: ["/api/organizations", currentUser?.organizationId] });
      toast({
        title: "Organization updated",
        description: "Organization settings have been successfully updated.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Update failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const sendBatchInvitesMutation = useMutation({
    mutationFn: async (emails: string[]) => {
      const response = await fetch(`/api/organizations/${currentUser?.organizationId}/invite-batch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emails }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to send invites");
      }
      return response.json() as Promise<{ results: { email: string; success: boolean; error?: string }[] }>;
    },
    onSuccess: (data) => {
      setInviteResults(data.results);
      setEmailChips([]);
      setEmailInputValue("");
      queryClient.invalidateQueries({
        queryKey: ["/api/organizations", currentUser?.organizationId, "pending-invites"],
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to send invites",
        description: error.message || "An error occurred.",
        variant: "destructive",
      });
    },
  });

  const generateInviteLinkMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`/api/organizations/${currentUser?.organizationId}/invite-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to generate invite link");
      }
      return response.json() as Promise<{ inviteUrl: string; token: string }>;
    },
    onSuccess: (data) => {
      setInviteLink(data.inviteUrl);
    },
    onError: (error: any) => {
      toast({
        title: "Failed to generate invite link",
        description: error.message || "An error occurred.",
        variant: "destructive",
      });
    },
  });

  const removeUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      const response = await fetch(`/api/organizations/${currentUser?.organizationId}/users/${userId}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to remove user");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/organizations", currentUser?.organizationId, "users"],
      });
      toast({
        title: "User removed",
        description: "The user has been successfully removed from the organization.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to remove user",
        description: error.message || "An error occurred while removing the user.",
        variant: "destructive",
      });
    },
  });

  const resendInviteMutation = useMutation({
    mutationFn: async (userId: string) => {
      const response = await fetch(`/api/organizations/${currentUser?.organizationId}/users/${userId}/resend-invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to resend invitation");
      }
      return response.json();
    },
    onSuccess: (_, userId) => {
      setJustResentInvite(userId);
      setTimeout(() => setJustResentInvite(null), 2000);
      toast({
        title: "Invitation resent",
        description: "The invitation email has been sent again.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to resend invitation",
        description: error.message || "An error occurred while resending the invitation.",
        variant: "destructive",
      });
    },
  });

  const getRoleBadgeVariant = (role: string) => {
    return role === "admin" ? "default" : "secondary";
  };

  const getRoleIcon = (role: string) => {
    return role === "admin" ? Shield : UserIcon;
  };

  const isInvitedUser = (userId: string) => {
    return userId.startsWith('invite_');
  };

  const getFirstName = (user: User) => {
    if (user.firstName) return user.firstName;
    if (user.email) return user.email.split('@')[0];
    return 'User';
  };

  const deleteOrgMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`/api/organizations/${currentUser?.organizationId}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to delete organization");
      }
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Organization deleted",
        description: "Your organization and all data have been removed. Redirecting to login...",
      });
      setTimeout(async () => {
        await fetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
        window.location.href = "/login";
      }, 1500);
    },
    onError: (error: Error) => {
      toast({
        title: "Delete failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  if (isLoading || !isAuthenticated || !currentUser) {
    return null;
  }

  const isAdmin = currentUser.role === 'admin';

  const orgForHeader: Organization = organization || {
    id: currentUser.organizationId,
    name: "Default Organization",
    slug: "default-organization",
    createdAt: new Date()
  };

  const canDelete = deleteConfirmText === organization?.name;

  return (
    <div className="min-h-screen bg-background">
      <DashboardHeader
        organization={orgForHeader}
        currentUser={currentUser}
      />
      
      <div className="w-full max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <Link href="/">
              <button className="h-7 w-7 flex items-center justify-center rounded hover:bg-accent text-muted-foreground hover:text-foreground transition-colors">
                <ArrowLeft className="h-4 w-4" />
              </button>
            </Link>
            <div>
              <h1 className="text-lg font-medium text-foreground">Organization Settings</h1>
              <p className="text-xs text-muted-foreground mt-0.5">Manage your organization</p>
            </div>
          </div>

          <Card className="border-border">
            <CardHeader className="pb-4">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                Organization
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isOrgLoading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              ) : isAdmin ? (
                <Form {...form}>
                  <form onSubmit={form.handleSubmit((data) => updateOrgMutation.mutate(data))} className="space-y-4">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs text-muted-foreground">Organization name</FormLabel>
                          <FormControl>
                            <Input placeholder="Organization name" {...field} className="h-9" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="slug"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs text-muted-foreground">Slug</FormLabel>
                          <FormControl>
                            <Input placeholder="my-org" {...field} className="h-9 font-mono" />
                          </FormControl>
                          <p className="text-[11px] text-muted-foreground">Lowercase letters, numbers, and hyphens only.</p>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {isSuccess && (
                      <Alert className="border-green-500/20 bg-green-500/5">
                        <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                        <AlertDescription className="text-green-600 dark:text-green-400 text-sm">
                          Updated successfully.
                        </AlertDescription>
                      </Alert>
                    )}

                    <Button 
                      type="submit" 
                      size="sm"
                      className="h-8"
                      disabled={updateOrgMutation.isPending}
                    >
                      {updateOrgMutation.isPending ? "Saving..." : "Save Changes"}
                    </Button>
                  </form>
                </Form>
              ) : (
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Organization name</p>
                    <p className="text-sm font-medium text-foreground">{organization?.name ?? "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Slug</p>
                    <p className="text-sm font-mono text-foreground">{organization?.slug ?? "—"}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {isAdmin && <Card className="border-border">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  Team
                  <span className="font-mono text-xs text-muted-foreground">{users.length}</span>
                </CardTitle>
                {currentUser.role === 'admin' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowInviteForm(true);
                      setInviteMode("email");
                      setInviteResults(null);
                      setEmailChips([]);
                      setEmailInputValue("");
                    }}
                    className="h-7 text-xs"
                    data-testid="button-show-invite-form"
                  >
                    <UserPlus className="h-3 w-3 mr-1" />
                    Invite
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {currentUser.role === 'admin' && showInviteForm && (
                <div className="border border-border rounded-lg p-3 mb-4 space-y-3 bg-muted/30">
                  {/* Header + close */}
                  <div className="flex items-center justify-between">
                    <div className="flex gap-1 p-0.5 bg-background rounded-md border border-border">
                      <button
                        onClick={() => { setInviteMode("email"); setInviteResults(null); }}
                        className={cn(
                          "flex items-center gap-1 text-xs px-2.5 py-1 rounded transition-colors",
                          inviteMode === "email"
                            ? "bg-accent text-foreground shadow-sm"
                            : "text-muted-foreground hover:text-foreground"
                        )}
                      >
                        <Mail className="h-3 w-3" />
                        Send by email
                      </button>
                      <button
                        onClick={() => {
                          setInviteMode("link");
                          if (!inviteLink) generateInviteLinkMutation.mutate();
                        }}
                        className={cn(
                          "flex items-center gap-1 text-xs px-2.5 py-1 rounded transition-colors",
                          inviteMode === "link"
                            ? "bg-accent text-foreground shadow-sm"
                            : "text-muted-foreground hover:text-foreground"
                        )}
                      >
                        <Link2 className="h-3 w-3" />
                        Copy link
                      </button>
                    </div>
                    <button
                      onClick={() => {
                        setShowInviteForm(false);
                        setInviteLink(null);
                        setEmailChips([]);
                        setEmailInputValue("");
                        setInviteResults(null);
                      }}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>

                  {/* ── Email mode ── */}
                  {inviteMode === "email" && (
                    <div className="space-y-2">
                      {/* Results view (after sending) */}
                      {inviteResults ? (
                        <div className="space-y-1.5">
                          {inviteResults.map((r) => (
                            <div key={r.email} className="flex items-center gap-2 text-xs">
                              {r.success ? (
                                <Check className="h-3.5 w-3.5 text-green-500 shrink-0" />
                              ) : (
                                <X className="h-3.5 w-3.5 text-destructive shrink-0" />
                              )}
                              <span className={r.success ? "text-foreground" : "text-destructive"}>{r.email}</span>
                              {!r.success && r.error && (
                                <span className="text-muted-foreground">— {r.error}</span>
                              )}
                            </div>
                          ))}
                          <button
                            onClick={() => { setInviteResults(null); }}
                            className="text-xs text-muted-foreground hover:text-foreground mt-1"
                          >
                            Invite more
                          </button>
                        </div>
                      ) : (
                        <>
                          {/* Chip input */}
                          <div className="flex flex-wrap gap-1 min-h-[32px] p-1.5 rounded-md border border-border bg-background focus-within:ring-1 focus-within:ring-ring">
                            {emailChips.map((email) => (
                              <span
                                key={email}
                                className="inline-flex items-center gap-1 bg-accent text-accent-foreground text-xs px-1.5 py-0.5 rounded"
                              >
                                {email}
                                <button
                                  type="button"
                                  onClick={() => setEmailChips((prev) => prev.filter((e) => e !== email))}
                                  className="text-muted-foreground hover:text-foreground"
                                >
                                  <X className="h-2.5 w-2.5" />
                                </button>
                              </span>
                            ))}
                            <input
                              type="text"
                              value={emailInputValue}
                              onChange={(e) => setEmailInputValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === "," || e.key === "Tab") {
                                  e.preventDefault();
                                  const val = emailInputValue.trim().replace(/,$/, "");
                                  if (val && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val) && !emailChips.includes(val)) {
                                    setEmailChips((prev) => [...prev, val]);
                                  }
                                  setEmailInputValue("");
                                } else if (e.key === "Backspace" && !emailInputValue && emailChips.length > 0) {
                                  setEmailChips((prev) => prev.slice(0, -1));
                                }
                              }}
                              onPaste={(e) => {
                                e.preventDefault();
                                const pasted = e.clipboardData.getData("text");
                                const parts = pasted.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
                                const valid = parts.filter(
                                  (p) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p) && !emailChips.includes(p)
                                );
                                if (valid.length) setEmailChips((prev) => [...prev, ...valid]);
                              }}
                              onBlur={() => {
                                const val = emailInputValue.trim().replace(/,$/, "");
                                if (val && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val) && !emailChips.includes(val)) {
                                  setEmailChips((prev) => [...prev, val]);
                                  setEmailInputValue("");
                                }
                              }}
                              placeholder={emailChips.length === 0 ? "name@example.com, another@example.com" : ""}
                              className="flex-1 min-w-[160px] text-xs bg-transparent outline-none placeholder:text-muted-foreground"
                            />
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Press Enter or comma to add each address. Each person gets their own invite link.
                          </p>
                          <Button
                            size="sm"
                            className="h-7 text-xs"
                            disabled={emailChips.length === 0 || sendBatchInvitesMutation.isPending}
                            onClick={() => sendBatchInvitesMutation.mutate(emailChips)}
                          >
                            {sendBatchInvitesMutation.isPending ? (
                              <><Loader2 className="h-3 w-3 mr-1.5 animate-spin" />Sending…</>
                            ) : (
                              <><Send className="h-3 w-3 mr-1.5" />Send {emailChips.length > 0 ? `${emailChips.length} invite${emailChips.length > 1 ? "s" : ""}` : "invites"}</>
                            )}
                          </Button>
                        </>
                      )}
                    </div>
                  )}

                  {/* ── Copy link mode ── */}
                  {inviteMode === "link" && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Link2 className="h-3 w-3" />
                        <span>Valid for 7 days, one use only. Each copy generates a fresh token.</span>
                      </div>
                      {generateInviteLinkMutation.isPending ? (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Generating link…
                        </div>
                      ) : inviteLink ? (
                        <div className="flex items-center gap-2">
                          <Input
                            readOnly
                            value={inviteLink}
                            className="h-7 text-xs font-mono bg-background flex-1 min-w-0"
                            onClick={(e) => (e.target as HTMLInputElement).select()}
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs shrink-0"
                            disabled={generateInviteLinkMutation.isPending}
                            onClick={() => {
                              generateInviteLinkMutation.mutate(undefined, {
                                onSuccess: (data) => {
                                  navigator.clipboard.writeText(data.inviteUrl).then(() => {
                                    setInviteLinkCopied(true);
                                    setTimeout(() => setInviteLinkCopied(false), 2000);
                                  });
                                },
                              });
                            }}
                          >
                            {inviteLinkCopied ? (
                              <Check className="h-3 w-3 text-green-500" />
                            ) : (
                              <Copy className="h-3 w-3" />
                            )}
                          </Button>
                        </div>
                      ) : null}
                      <p className="text-xs text-muted-foreground">
                        Share this link in Slack, email, or anywhere. The recipient creates their own password when signing up.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {isUsersLoading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              ) : users.length === 0 ? (
                <div className="text-center py-6">
                  <Users className="h-5 w-5 text-muted-foreground/40 mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground">No team members</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {/* Pending email-invite rows */}
                  {pendingInvites.map((invite) => (
                    <div
                      key={invite.token}
                      className="flex items-center justify-between gap-2 px-2.5 py-2 rounded bg-amber-500/5"
                    >
                      <div className="flex items-center gap-2.5 min-w-0 flex-1">
                        <div className="h-6 w-6 rounded bg-muted flex items-center justify-center shrink-0">
                          <span className="text-[10px] font-medium text-muted-foreground">
                            {(invite.targetEmail || '?').charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <span className="text-[11px] text-muted-foreground truncate block">{invite.targetEmail}</span>
                        </div>
                      </div>
                      <Badge
                        variant="outline"
                        className="text-[10px] px-1.5 py-0 h-5 border-amber-500/30 text-amber-600 dark:text-amber-400 shrink-0"
                      >
                        <Mail className="h-2.5 w-2.5 mr-0.5" />
                        Invited
                      </Badge>
                    </div>
                  ))}

                  {users.map((user) => {
                    const RoleIcon = getRoleIcon(user.role);
                    return (
                      <div
                        key={user.id}
                        className="flex items-center justify-between gap-2 px-2.5 py-2 rounded hover:bg-accent/50 transition-colors"
                        data-testid={`team-member-${user.email}`}
                      >
                        <div className="flex items-center gap-2.5 min-w-0 flex-1">
                          <div className="h-6 w-6 rounded bg-muted flex items-center justify-center shrink-0">
                            <span className="text-[10px] font-medium text-muted-foreground">
                              {(user.firstName || user.email || 'U').charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm font-medium truncate" data-testid={`team-member-name-${user.email}`}>
                                {getFirstName(user)}
                              </span>
                              {user.id === currentUser.id && (
                                <span className="text-[10px] text-muted-foreground">(you)</span>
                              )}
                            </div>
                            <span className="text-[11px] text-muted-foreground truncate block" data-testid={`team-member-email-${user.email}`}>
                              {user.email}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <Badge
                            variant={isInvitedUser(user.id) ? "outline" : getRoleBadgeVariant(user.role)}
                            className={`text-[10px] px-1.5 py-0 h-5 ${isInvitedUser(user.id) ? 'border-amber-500/30 text-amber-600 dark:text-amber-400' : ''}`}
                          >
                            <RoleIcon className="h-2.5 w-2.5 mr-0.5" />
                            {isInvitedUser(user.id) ? 'Invited' : user.role === 'admin' ? 'Admin' : 'Mgr'}
                          </Badge>
                          {user.id !== currentUser.id && currentUser.role === 'admin' && (
                            <div className="flex items-center gap-0.5">
                              {isInvitedUser(user.id) && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0"
                                  onClick={() => resendInviteMutation.mutate(user.id)}
                                  disabled={resendInviteMutation.isPending}
                                  title="Resend invitation"
                                  data-testid={`button-resend-invite-${user.email}`}
                                >
                                  {justResentInvite === user.id ? (
                                    <Check className="h-3 w-3 text-green-500" />
                                  ) : (
                                    <Send className="h-3 w-3 text-muted-foreground" />
                                  )}
                                </Button>
                              )}
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0"
                                    title="Remove user"
                                    data-testid={`button-remove-user-${user.email}`}
                                  >
                                    <X className="h-3 w-3 text-muted-foreground" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md">
                                  <AlertDialogHeader>
                                    <AlertDialogTitle className="text-base">Remove User</AlertDialogTitle>
                                    <AlertDialogDescription className="text-sm">
                                      Remove <strong>{getFirstName(user)}</strong> from the organization? This cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel className="h-8 text-sm">Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => removeUserMutation.mutate(user.id)}
                                      className="bg-destructive hover:bg-destructive/90 h-8 text-sm"
                                      disabled={removeUserMutation.isPending}
                                    >
                                      {removeUserMutation.isPending ? 'Removing...' : 'Remove'}
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>}

          {isAdmin && <Card className="border-border">
            <CardHeader className="pb-4">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                Notifications
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isNotifLoading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              ) : notificationSettings ? (
                <div className="space-y-4">
                  {[
                    { key: "commentAdded" as const, scopeKey: "commentAddedScope" as const, label: "New Comments", description: "When someone posts a comment" },
                    { key: "userMentioned" as const, scopeKey: "userMentionedScope" as const, label: "Mentions", description: "When a user is @mentioned" },
                    { key: "userJoinedDocument" as const, scopeKey: "userJoinedDocumentScope" as const, label: "User Joined", description: "When someone new joins a document" },
                    { key: "userInvitation" as const, scopeKey: "userInvitationScope" as const, label: "Invitations", description: "Invitation emails for new users" },
                    { key: "versionComparisonReady" as const, scopeKey: "versionComparisonReadyScope" as const, label: "Comparison Ready", description: "When version comparison finishes" },
                  ].map(({ key, scopeKey, label, description }) => (
                    <div key={key} className="flex items-center justify-between gap-3 py-2 border-b border-border last:border-0 last:pb-0">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <Switch
                          checked={notificationSettings[key] as boolean}
                          onCheckedChange={(checked) => {
                            updateNotifMutation.mutate({ [key]: checked });
                          }}
                          disabled={updateNotifMutation.isPending}
                          className="shrink-0"
                        />
                        <div className="min-w-0">
                          <p className="text-sm font-medium leading-none">{label}</p>
                          <p className="text-[11px] text-muted-foreground mt-0.5">{description}</p>
                        </div>
                      </div>
                      <Select
                        value={notificationSettings[scopeKey] as string}
                        onValueChange={(value) => {
                          updateNotifMutation.mutate({ [scopeKey]: value });
                        }}
                        disabled={!notificationSettings[key] || updateNotifMutation.isPending}
                      >
                        <SelectTrigger className="w-[160px] h-7 text-[11px] shrink-0">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all_users">All Users</SelectItem>
                          <SelectItem value="admins_only">Admins Only</SelectItem>
                          <SelectItem value="document_participants">Participants</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ))}

                  <Accordion type="single" collapsible className="border-t border-border pt-2 -mx-1">
                    <AccordionItem value="email-activity" className="border-0">
                      <AccordionTrigger className="px-1 py-2 text-xs font-medium text-muted-foreground hover:text-foreground hover:no-underline [&>svg]:h-3.5 [&>svg]:w-3.5">
                        Email Activity Log
                      </AccordionTrigger>
                      <AccordionContent className="px-1 pt-1 pb-0">
                        {currentUser?.organizationId && (
                          <EmailActivityLog organizationId={currentUser.organizationId} />
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Unable to load notification settings.</p>
              )}
            </CardContent>
          </Card>}

          {isAdmin && <Card className="border-destructive/20">
            <CardHeader className="pb-4">
              <CardTitle className="text-sm font-medium flex items-center gap-2 text-destructive">
                <Trash2 className="h-4 w-4" />
                Danger Zone
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!showDeleteConfirm ? (
                <Button
                  variant="destructive"
                  size="sm"
                  className="h-8"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  Delete Organization
                </Button>
              ) : (
                <div className="space-y-3">
                  <Alert variant="destructive">
                    <AlertCircle className="h-3.5 w-3.5" />
                    <AlertDescription className="text-sm">
                      This permanently deletes all data. You will be logged out.
                    </AlertDescription>
                  </Alert>

                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">
                      Type <span className="font-mono font-medium text-foreground">{organization?.name}</span> to confirm:
                    </p>
                    <Input
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder="Organization name"
                      className="h-8 text-sm"
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="destructive"
                      size="sm"
                      className="h-8"
                      disabled={!canDelete || deleteOrgMutation.isPending}
                      onClick={() => deleteOrgMutation.mutate()}
                    >
                      {deleteOrgMutation.isPending ? "Deleting..." : "Delete Everything"}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8"
                      onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(""); }}
                      disabled={deleteOrgMutation.isPending}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>}
        </div>
      </div>
    </div>
  );
}
